import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Chip,
  useTheme,
} from '@mui/material';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import BoltIcon from '@mui/icons-material/Bolt';
import { useAuth } from '../../../contexts/AuthContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

interface BatteryData {
  timestamp: string;
  voltage: number;
  stateOfCharge?: number;   // raw telemetry SOC (may be inaccurate for non-default battery types)
  calculatedSOC?: number;   // voltage-derived SOC using battery type lookup table
  status?: string;
  deviceName?: string;
}

interface BatteryChartTabProps {
  deviceId: string;
  includeChildren?: boolean;
  deviceSettings?: { settingName: string; settingValue: string }[];
}

const DEVICE_COLORS = ['#4fc3f7', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#f97316'];

// ─── Battery SOC lookup tables ────────────────────────────────────────────────
// Each table: [voltage, soc%] pairs sorted voltage descending (100% → 0%)

/** LiFePO4 — 4S pack (nominal ≈12.8V). Very flat curve. */
const LIFEPO4_TABLE: [number, number][] = [
  [14.6, 100], [13.4, 99], [13.3, 90], [13.2, 70],
  [13.15, 50], [13.1, 30], [13.0, 20], [12.9, 10], [12.0, 0],
];
/** Lead-Acid — 12V nominal */
const LEAD_ACID_TABLE: [number, number][] = [
  [12.7, 100], [12.5, 75], [12.4, 50], [12.2, 25], [12.0, 0],
];
/** Li-Ion — generic 3S */
const LIION_TABLE: [number, number][] = [
  [12.6, 100], [12.3, 80], [11.9, 50], [11.5, 20], [10.5, 0],
];

function getSOCTable(type: string): [number, number][] {
  const t = (type ?? '').toLowerCase().replace(/[\s_-]/g, '');
  if (t.includes('lifepo') || t.includes('lfp'))                      return LIFEPO4_TABLE;
  if (t.includes('leadacid') || t.includes('sla') || t.includes('lead')) return LEAD_ACID_TABLE;
  if (t.includes('liion') || t.includes('lipo'))                      return LIION_TABLE;
  return LIFEPO4_TABLE; // safe default
}

/** Piecewise-linear interpolation through a voltage→SOC lookup table */
function calculateSOC(
  voltage: number,
  batteryType: string,
  minVOverride?: number,
  maxVOverride?: number,
): number {
  const table = getSOCTable(batteryType);
  const maxV = maxVOverride ?? table[0][0];
  const minV = minVOverride ?? table[table.length - 1][0];
  if (voltage >= maxV) return 100;
  if (voltage <= minV) return 0;
  for (let i = 0; i < table.length - 1; i++) {
    const [v1, soc1] = table[i];
    const [v2, soc2] = table[i + 1];
    if (voltage <= v1 && voltage >= v2) {
      const t = (voltage - v2) / (v1 - v2);
      return Math.round(soc2 + t * (soc1 - soc2));
    }
  }
  return 0;
}

/** Voltage colour thresholds per battery type */
function getVoltageThresholds(type: string): { good: number; warn: number } {
  const t = (type ?? '').toLowerCase().replace(/[\s_-]/g, '');
  if (t.includes('leadacid') || t.includes('sla') || t.includes('lead')) return { good: 12.5, warn: 11.8 };
  return { good: 13.1, warn: 12.9 }; // LiFePO4 / default
}

/** Reference lines on the voltage chart per battery type */
function getVoltageReferenceLines(type: string): { value: number; label: string; color: string }[] {
  const t = (type ?? '').toLowerCase().replace(/[\s_-]/g, '');
  if (t.includes('leadacid') || t.includes('sla') || t.includes('lead')) return [
    { value: 12.5, label: '12.5V (75%)', color: '#10b981' },
    { value: 12.0, label: '12.0V (0%)',  color: '#ef4444' },
  ];
  return [ // LiFePO4 default
    { value: 13.3, label: '13.3V (~90%)', color: '#10b981' },
    { value: 13.1, label: '13.1V (~30%)', color: '#f59e0b' },
    { value: 12.9, label: '12.9V (~10%)', color: '#ef4444' },
  ];
}

const BatteryChartTab: React.FC<BatteryChartTabProps> = ({ deviceId, includeChildren = false, deviceSettings }) => {
  const auth = useAuth();
  const theme = useTheme();
  const accessToken = auth.tokens?.accessToken;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [batteryData, setBatteryData] = useState<BatteryData[]>([]);
  const [timeRange, setTimeRange] = useState<string>('7d');

  const authHeader = useMemo(() => ({ Authorization: `Bearer ${accessToken}` }), [accessToken]);

  // Extract battery configuration from device settings
  const batteryConfig = useMemo(() => {
    const type = deviceSettings?.find(s => s.settingName === 'device.battery.type')?.settingValue ?? 'LiFePo';
    const minV = parseFloat(deviceSettings?.find(s => s.settingName === 'device.battery.minVoltage')?.settingValue ?? '');
    const maxV = parseFloat(deviceSettings?.find(s => s.settingName === 'device.battery.maxVoltage')?.settingValue ?? '');
    return {
      type,
      minVoltage: isNaN(minV) ? undefined : minV,
      maxVoltage: isNaN(maxV) ? undefined : maxV,
    };
  }, [deviceSettings]);

  const loadBatteryData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ skip: '0', take: '2000', sortBy: 'dateCreated', sortOrder: 'desc' });
      if (includeChildren) params.append('includeChildren', 'true');
      const res = await fetch(`/api/devices/${deviceId}/events?${params}`, { headers: authHeader });
      if (!res.ok) throw new Error('Failed to fetch events');

      const responseData = await res.json();
      const events = responseData.data || [];

      const data: BatteryData[] = [];

      for (const event of events) {
        try {
          let parsed: any = null;
          if (event.name) {
            try { parsed = JSON.parse(event.name); } catch { /* not JSON */ }
          }
          if (!parsed) continue;

          // Support multiple data shapes:
          // { data: { voltage: 12.95, state_of_charge: 100, status: "Discharging" } }
          // { voltage: 12.95, state_of_charge: 100 }
          // { data: { battery_voltage: 12.95 } }
          const d = parsed.data ?? parsed;
          const voltage = d?.voltage ?? d?.battery_voltage ?? d?.batteryVoltage;
          if (typeof voltage === 'number') {
            const rawSOC = d?.state_of_charge ?? d?.stateOfCharge ?? d?.soc;
            data.push({
              timestamp: event.dateCreated,
              voltage,
              stateOfCharge: typeof rawSOC === 'number' ? rawSOC : undefined,
              calculatedSOC: calculateSOC(voltage, batteryConfig.type, batteryConfig.minVoltage, batteryConfig.maxVoltage),
              status: d?.status ?? parsed?.status ?? undefined,
              deviceName: event.deviceName || undefined,
            });
          }
        } catch {
          continue;
        }
      }

      // Filter by time range
      const now = new Date();
      const filtered = data.filter(item => {
        const diff = now.getTime() - new Date(item.timestamp).getTime();
        switch (timeRange) {
          case '1h':  return diff <= 60 * 60 * 1000;
          case '6h':  return diff <= 6 * 60 * 60 * 1000;
          case '24h': return diff <= 24 * 60 * 60 * 1000;
          case '7d':  return diff <= 7 * 24 * 60 * 60 * 1000;
          case '30d': return diff <= 30 * 24 * 60 * 60 * 1000;
          default: return true;
        }
      });

      // Sort ascending for chart
      filtered.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      setBatteryData(filtered);
    } catch (e: any) {
      setError(e.message || 'Failed to load battery data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBatteryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, timeRange, includeChildren, batteryConfig]);

  const deviceNames = useMemo(() => {
    const names = new Set<string>();
    batteryData.forEach(d => { if (d.deviceName) names.add(d.deviceName); });
    return Array.from(names);
  }, [batteryData]);

  const hasSOC = batteryData.some(d => d.calculatedSOC !== undefined);
  const hasReportedSOC = batteryData.some(d => d.stateOfCharge !== undefined);

  // Build chart data — for multi-device use device name as key
  const voltageChartData = useMemo(() => {
    if (includeChildren && deviceNames.length > 1) {
      return batteryData.map(item => ({
        timestamp: new Date(item.timestamp).toLocaleTimeString(),
        fullTimestamp: item.timestamp,
        [item.deviceName || 'Unknown']: item.voltage,
        status: item.status,
      }));
    }
    return batteryData.map(item => ({
      timestamp: new Date(item.timestamp).toLocaleTimeString(),
      fullTimestamp: item.timestamp,
      voltage: item.voltage,
      stateOfCharge: item.stateOfCharge,
      status: item.status,
    }));
  }, [batteryData, includeChildren, deviceNames]);

  const socChartData = useMemo(() => {
    if (includeChildren && deviceNames.length > 1) {
      return batteryData
        .filter(d => d.calculatedSOC !== undefined)
        .map(item => ({
          timestamp: new Date(item.timestamp).toLocaleTimeString(),
          fullTimestamp: item.timestamp,
          [item.deviceName || 'Unknown']: item.calculatedSOC,
          status: item.status,
        }));
    }
    return batteryData
      .filter(d => d.calculatedSOC !== undefined)
      .map(item => ({
        timestamp: new Date(item.timestamp).toLocaleTimeString(),
        fullTimestamp: item.timestamp,
        calculatedSOC: item.calculatedSOC,
        reportedSOC: item.stateOfCharge,  // raw telemetry, shown as dim reference
        status: item.status,
      }));
  }, [batteryData, includeChildren, deviceNames]);

  // Latest stats per device (or single)
  const latestStats = useMemo(() => {
    if (batteryData.length === 0) return [];
    if (includeChildren && deviceNames.length > 1) {
      return deviceNames.map(name => {
        const forDevice = batteryData.filter(d => d.deviceName === name);
        const latest = forDevice[forDevice.length - 1];
        return { name, ...latest };
      });
    }
    const latest = batteryData[batteryData.length - 1];
    return [{ name: 'Battery', ...latest }];
  }, [batteryData, deviceNames, includeChildren]);

  const formatTimestamp = (ts: string) => new Date(ts).toLocaleString();

  const getVoltageColor = (v: number) => {
    const { good, warn } = getVoltageThresholds(batteryConfig.type);
    if (v >= good) return '#10b981';
    if (v >= warn) return '#f59e0b';
    return '#ef4444';
  };

  const getSocColor = (soc: number) => {
    if (soc >= 50) return '#10b981';
    if (soc >= 20) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BatteryChargingFullIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6">Battery Chart</Typography>
        </Box>
        <FormControl sx={{ minWidth: 150 }} size="small">
          <InputLabel>Time Range</InputLabel>
          <Select value={timeRange} label="Time Range" onChange={(e) => setTimeRange(e.target.value)}>
            <MenuItem value="1h">Last Hour</MenuItem>
            <MenuItem value="6h">Last 6 Hours</MenuItem>
            <MenuItem value="24h">Last 24 Hours</MenuItem>
            <MenuItem value="7d">Last 7 Days</MenuItem>
            <MenuItem value="30d">Last 30 Days</MenuItem>
            <MenuItem value="all">All Time</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : batteryData.length === 0 ? (
        <Alert severity="info">
          No battery voltage data found for this device in the selected time range.
          Battery events are expected in the format: <code>{`{ "data": { "voltage": 12.95, "state_of_charge": 100, "status": "Discharging" } }`}</code>
        </Alert>
      ) : (
        <Box>
          {/* Latest Status Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {latestStats.map((stat, i) => (
              <Grid item xs={12} sm={includeChildren && deviceNames.length > 1 ? 6 : 4} key={i}>
                <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                  {includeChildren && deviceNames.length > 1 && (
                    <Typography variant="caption" sx={{ color: DEVICE_COLORS[i % DEVICE_COLORS.length], fontWeight: 600 }}>
                      {stat.name}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <BoltIcon sx={{ fontSize: 16, color: getVoltageColor(stat.voltage) }} />
                        <Typography variant="h5" sx={{ fontWeight: 700, color: getVoltageColor(stat.voltage) }}>
                          {stat.voltage?.toFixed(2)}V
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">VOLTAGE</Typography>
                    </Box>
                    {stat.calculatedSOC !== undefined && (
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: getSocColor(stat.calculatedSOC!) }}>
                          {stat.calculatedSOC}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">EST. SOC ({batteryConfig.type})</Typography>
                        {stat.stateOfCharge !== undefined && (
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', fontSize: '0.65rem' }}>
                            Reported: {stat.stateOfCharge}%
                          </Typography>
                        )}
                      </Box>
                    )}
                    {stat.status && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip
                          label={stat.status}
                          size="small"
                          sx={{
                            backgroundColor: stat.status.toLowerCase().includes('charg') ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
                            color: stat.status.toLowerCase().includes('charg') ? '#10b981' : '#f59e0b',
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Voltage Chart */}
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Voltage Over Time</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={voltageChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="timestamp" tick={{ fontSize: '0.7rem' }} />
              <YAxis
                domain={['auto', 'auto']}
                label={{ value: 'Voltage (V)', angle: -90, position: 'insideLeft' }}
                tickFormatter={(v) => `${v}V`}
              />
              {getVoltageReferenceLines(batteryConfig.type).map(ref => (
                <ReferenceLine key={ref.value} y={ref.value} stroke={ref.color} strokeDasharray="4 4"
                  label={{ value: ref.label, fill: ref.color, fontSize: 11, dy: 14 }} />
              ))}
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const d = payload[0].payload;
                    return (
                      <Box sx={{ bgcolor: 'background.paper', p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <Typography variant="body2"><strong>Time:</strong> {formatTimestamp(d.fullTimestamp)}</Typography>
                        {payload.map((p: any, idx: number) => (
                          <Typography key={idx} variant="body2" sx={{ color: p.color }}>
                            <strong>{p.name}:</strong> {typeof p.value === 'number' ? `${p.value.toFixed(2)}V` : p.value}
                          </Typography>
                        ))}
                        {d.status && <Typography variant="body2"><strong>Status:</strong> {d.status}</Typography>}
                      </Box>
                    );
                  }
                  return null;
                }}
              />
              <Legend />
              {includeChildren && deviceNames.length > 1
                ? deviceNames.map((name, i) => (
                    <Line key={name} type="monotone" dataKey={name} stroke={DEVICE_COLORS[i % DEVICE_COLORS.length]}
                      strokeWidth={2} dot={false} activeDot={{ r: 4 }} name={name} connectNulls />
                  ))
                : <Line type="monotone" dataKey="voltage" stroke="#4fc3f7" strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="Voltage" />
              }
            </LineChart>
          </ResponsiveContainer>

          {/* State of Charge Chart (voltage-derived) */}
          {hasSOC && socChartData.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Estimated State of Charge</Typography>
                <Typography variant="caption" color="text.secondary">(calculated from voltage · {batteryConfig.type})</Typography>
              </Box>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={socChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="timestamp" tick={{ fontSize: '0.7rem' }} />
                  <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} label={{ value: 'SOC (%)', angle: -90, position: 'insideLeft' }} />
                  <ReferenceLine y={30} stroke="#f59e0b" strokeDasharray="4 4" />
                  <ReferenceLine y={10} stroke="#ef4444" strokeDasharray="4 4" />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const d = payload[0].payload;
                        return (
                          <Box sx={{ bgcolor: 'background.paper', p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                            <Typography variant="body2"><strong>Time:</strong> {formatTimestamp(d.fullTimestamp)}</Typography>
                            {payload.map((p: any, idx: number) => (
                              <Typography key={idx} variant="body2" sx={{ color: p.color }}>
                                <strong>{p.name}:</strong> {p.value}%
                              </Typography>
                            ))}
                          </Box>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend />
                  {includeChildren && deviceNames.length > 1
                    ? deviceNames.map((name, i) => (
                        <Line key={name} type="monotone" dataKey={name} stroke={DEVICE_COLORS[i % DEVICE_COLORS.length]}
                          strokeWidth={2} dot={false} activeDot={{ r: 4 }} name={name} connectNulls />
                      ))
                    : (
                        <>
                          <Line type="monotone" dataKey="calculatedSOC" stroke="#f59e0b" strokeWidth={2}
                            dot={false} activeDot={{ r: 4 }} name="Estimated SOC" />
                          {hasReportedSOC && (
                            <Line type="monotone" dataKey="reportedSOC" stroke="#6b7280" strokeWidth={1.5}
                              strokeDasharray="4 4" dot={false} activeDot={{ r: 3 }} name="Reported SOC (telemetry)" />
                          )}
                        </>
                      )
                  }
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {batteryData.length} battery readings
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BatteryChartTab;
